import { Component, OnInit, OnDestroy, ViewChild, ViewChildren, QueryList, ElementRef, ChangeDetectorRef, NgZone, HostListener } from '@angular/core';
import { DomSanitizer, SafeHtml, Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AiService } from 'src/app/service/ai.service';
import { AccountService } from 'src/app/service/account.service';
import { AuthService } from 'src/app/service/auth.service';
import { FileService } from 'src/app/service/file.service';
import { VcuService } from 'src/app/service/vcu.service';
import { VcuFileItem } from 'src/app/model/response/vcu-files-response';
import { PlanFeatureService } from 'src/app/service/plan-feature.service';
import { AiSession, AiSessionDetail, AiSessionDetailSession, AiMessage, AiChatRequest, AiProfileOption, AiProfileEvent, FtcfArtifact, FtcfFile, DatalogChannelsArtifact, normalizeDatalogArtifact, ClarificationData, ClarificationQuestion, TraceEvent, ProgressStep } from 'src/app/model/response/ai-chat-response';
import { AiErrorResponse, AI_ERROR_CODES } from 'src/app/model/ai-error';
import { AiCreditType, AiFreeTank, formatRefillTime, isFreeTankInUse, isPaidPlan } from 'src/app/model/response/ai-response';
import { forkJoin, of, lastValueFrom, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { marked } from 'marked';
import { AiSkill, AiSkillUpsert } from 'src/app/model/response/ai-skill.model';
import { PlanModalService } from 'src/app/share/plan-modal.service';
import { ChatAttachment } from 'src/app/model/chat-attachment';
import { uploadRejectionKey } from 'src/app/shared/upload/upload-rejection';
import { DragRunArtifact, normalizeDragRunArtifact } from 'src/app/model/response/ai-chat-response';
import { MapArtifactPayload, CorrectionArtifactPayload } from 'src/app/model/response/ai-chat-response';
import { heatmapCellStyle } from '../heatmap';

export function detectAttachmentType(fileName: string | null | undefined, explicitType?: string | null): 'map' | 'log' {
  if (explicitType === 'map' || explicitType === 'log') return explicitType;
  const ext = (fileName ?? '').split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'ftml' || ext === 'ftlog') return 'log';
  return 'map';
}

export function composeMessageWithAttachments(userText: string, attachments: ChatAttachment[], attachmentsHeader: string): string {
  const readyNames = attachments.filter(a => a.status === 'ready').map(a => a.fileName);
  if (!readyNames.length) return userText;
  const list = readyNames.map(name => `- ${name}`).join('\n');
  const block = `${attachmentsHeader}\n${list}`;
  return userText ? `${userText}\n\n${block}` : block;
}

/**
 * `context.log_info` do AiChatRequest (mesmo formato do app mobile): com o Guid a IA lê o
 * arquivo direto, sem procurar pelo nome — a busca por nome só enxerga logs, nunca mapas.
 * Só com UM anexo pronto e com id; com vários, vale a lista de nomes no texto.
 * `file_name` vai com a extensão: é por ela que a IA distingue mapa de log.
 */
export function buildLogInfo(attachments: ChatAttachment[]): { log_id: string; file_name: string } | null {
  const ready = attachments.filter(a => a.status === 'ready');
  if (ready.length !== 1 || !ready[0].fileId) return null;
  return { log_id: ready[0].fileId, file_name: ready[0].fileName };
}

/**
 * Texto que o usuário vê na própria bolha ao responder as perguntas de esclarecimento.
 *
 * O que vai para a IA é `[cabeçalho]\n- pergunta: resposta` por linha (ver
 * `sendClarification`): a pergunta segue no payload porque é ela que dá sentido à resposta
 * para o modelo. Na tela, porém, o usuário acabou de ver a pergunta na bolha da IA — repetir
 * só polui. Aqui fica apenas o que ele escolheu, uma resposta por linha.
 *
 * Devolve `null` quando o conteúdo não tem esse formato, para o chamador exibir o texto
 * original. Independe do idioma: reconhece o cabeçalho pelos colchetes, não pelo texto.
 */
export function clarificationAnswersForDisplay(content: string): string | null {
  const lines = content.split('\n');
  if (lines.length < 2 || !/^\[[^\]]+\]$/.test(lines[0].trim())) return null;
  const answers: string[] = [];
  for (const raw of lines.slice(1)) {
    const line = raw.trim();
    if (!line) continue;
    if (!line.startsWith('- ')) return null;
    const body = line.slice(2);
    // A pergunta normalmente termina em "?": corta depois do último "?: ". Sem "?", o
    // primeiro ": " separa pergunta de resposta.
    const q = body.lastIndexOf('?: ');
    const sep = q >= 0 ? q + 1 : body.indexOf(': ');
    answers.push(sep >= 0 ? body.slice(sep + 2).trim() : body);
  }
  return answers.length ? answers.join('\n') : null;
}

/** Contrato do streaming: sem `replace` acrescenta; com `replace: true` descarta o texto deste
 *  turno e usa o do evento. Depois de um `guardrail` bloqueado, o texto do web fica e o resto é ignorado. */
export function applyContentEvent(current: string, data: unknown, guardrailBlocked: boolean): string {
  if (guardrailBlocked || data === null || typeof data !== 'object') return current;
  const d = data as { text?: unknown; replace?: unknown };
  const text = typeof d.text === 'string' ? d.text : '';
  return d.replace === true ? text : current + text;
}

/**
 * Acrescenta uma corrida à lista do turno. O contrato (Seção 1) permite vários artifacts
 * `drag-run` no mesmo turno, um por corrida — guardar em campo único descartaria as
 * seguintes, que é o motivo de isto ser uma lista e não um `=`.
 */
export function appendDragRun(
  current: DragRunArtifact[] | undefined,
  payload: unknown,
): DragRunArtifact[] {
  return [...(current ?? []), normalizeDragRunArtifact(payload)];
}

/**
 * Copy por tipo de crédito (`<base>.bonus`, `<base>.purchased`...). Sem o tipo — backend
 * anterior, ou `reserve`, que nunca chega aqui mas não tem frase própria — cai em `<base>.generic`.
 */
function creditTypeKey(base: string, type: AiCreditType | null | undefined): string {
  return type && type !== 'reserve' ? `${base}.${type}` : `${base}.generic`;
}

function parseHeatmapNumber(raw: string): number | null {
  const cleaned = raw.trim().replace(',', '.');
  if (!/^-?\d+(\.\d+)?$/.test(cleaned)) return null;
  const value = parseFloat(cleaned);
  return Number.isFinite(value) ? value : null;
}

// Tabelas markdown numéricas (mapas de injeção/ignição) recebem heatmap por valor;
// a primeira coluna é tratada como rótulo (RPM) e não é colorida.
marked.use({
  renderer: {
    table(token) {
      let min = Infinity;
      let max = -Infinity;
      for (const row of token.rows) {
        for (let c = 1; c < row.length; c++) {
          const n = parseHeatmapNumber(row[c].text);
          if (n !== null) {
            if (n < min) min = n;
            if (n > max) max = n;
          }
        }
      }
      const hasRange = Number.isFinite(min) && Number.isFinite(max);

      const headerHtml = token.header
        .map((cell) => `<th${cell.align ? ` align="${cell.align}"` : ''}>${this.parser.parseInline(cell.tokens)}</th>`)
        .join('');

      const bodyHtml = token.rows
        .map((row) => {
          const cellsHtml = row
            .map((cell, c) => {
              let style = '';
              if (c > 0 && hasRange) {
                const n = parseHeatmapNumber(cell.text);
                if (n !== null) style = ` style="${heatmapCellStyle(n, min, max)}"`;
              }
              return `<td${cell.align ? ` align="${cell.align}"` : ''}${style}>${this.parser.parseInline(cell.tokens)}</td>`;
            })
            .join('');
          return `<tr>${cellsHtml}</tr>`;
        })
        .join('');

      return `<table><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table>`;
    },
  },
});

/** Ponto de partida oferecido na tela vazia. `labelKey` para texto estático
 *  (passa pelo transloco no template, então acompanha troca de idioma);
 *  `label` para nome vindo da API, que não é traduzível. */
interface ChatStarter {
  id: string;
  icon: string;
  label?: string;
  labelKey?: string;
  run: () => void;
  /** Exige plano pago: o botão fica inerte e recebe a tag. */
  locked?: boolean;
  skill?: boolean;
}

interface ChatAction {
  key: string;
  section?: 'conversation' | 'agents' | 'model' | 'tools';
  label: string;
  note?: string;
  mono?: boolean;
  checked?: boolean;
  disabled?: boolean;
  run: () => void;
}

const SECTION_ORDER: NonNullable<ChatAction['section']>[] = ['conversation', 'agents', 'model', 'tools'];

const foldText = (s: string): string => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

@Component({
  selector: 'app-chatbot',
  templateUrl: './chatbot.component.html',
  styleUrls: ['./chatbot.component.css'],
})
export class ChatbotComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  @ViewChild('chatScroll') chatScrollEl!: ElementRef;
  @ViewChild('chatInputEl') chatInputEl?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('clarifyOtherEl') clarifyOtherEl?: ElementRef<HTMLInputElement>;
  @ViewChildren('clarifyTabEl') clarifyTabEls?: QueryList<ElementRef<HTMLElement>>;

  sessions: AiSession[] = [];
  sessionsLoading = false;
  nextCursor: string | null = null;

  currentSessionId: string | null = null;
  messages: AiMessage[] = [];
  messagesLoading = false;

  isAdminUser = false;
  isExportingConversation = false;

  // ─── Trace (debug) mode ───────────────────────────────────────────
  get isTraceAdmin(): boolean {
    return this.authService.getAdminRole() !== null;
  }

  get traceModeActive(): boolean {
    return sessionStorage.getItem('ai_trace') === '1';
  }

  isStreaming = false;
  streamingContent = '';
  streamingGuardrailBlocked = false;
  thinkingContent = '';
  activeToolCalls: { tool_call_id: string; tool_name: string }[] = [];
  streamingAgentHandoff: { from: string; to: string } | null = null;
  streamingTraceEvents: TraceEvent[] = [];
  /** Narração pública de progresso do turno em andamento (evento SSE `progress`). */
  streamingProgressSteps: ProgressStep[] = [];
  /** Ids (message_id) com a lista de progresso reexpandida no histórico. */
  expandedProgressIds = new Set<string>();
  clarificationData: ClarificationData | null = null;
  clarificationAnswers: Map<string, string[]> = new Map();
  /** Texto de "Outro", por pergunta. Preenchido pelo campo que aparece
   *  dentro da propria caixa quando "Outro" e escolhido. */
  clarificationOtherMap: Record<string, string> = {};
  /** Sentinela de "Outro" na lista de respostas. Nao e rotulo de opcao real:
   *  marca que aquela pergunta sera respondida com o texto do campo do chat. */
  private static readonly OTHER = '__ft_other__';
  currentClarificationTab = 0;
  private streamingFtcfArtifact: FtcfArtifact | null = null;
  private streamingFtcfFile: FtcfFile | null = null;
  streamingDatalogChart: DatalogChannelsArtifact | null = null;
  streamingDragRuns: DragRunArtifact[] = [];
  /** O contrato aceita vários gráficos por turno, então acumula em vez de substituir. */
  streamingCharts: unknown[] = [];
  streamingMapArtifacts: MapArtifactPayload[] = [];
  streamingCorrectionArtifacts: CorrectionArtifactPayload[] = [];
  expandedThinkingIds = new Set<string>();

  userInput = '';

  // ─── Seletor de perfis (Eco/Sport/Race) ─────────────────────────────
  /** Altura maxima do campo de digitar, em px. Precisa ser aplicada INLINE: o
   *  autoResize do PrimeNG decide se mostra a barra comparando
   *  `el.style.maxHeight` — a propriedade inline, nao o estilo computado. Com o
   *  valor so na folha de estilo ele le '', parseFloat da NaN, toda comparacao
   *  com NaN e falsa, e o campo acabava sempre com overflow:hidden.
   *
   *  Acompanha a janela: 240px fixos ocupavam mais da metade da tela num
   *  celular deitado, e como o compositor nao encolhe (flex-shrink:0) o que
   *  sumia era a conversa. */
  inputMaxHeightPx = ChatbotComponent.computeInputMaxHeight();

  private static computeInputMaxHeight(): number {
    return Math.max(96, Math.min(240, Math.round(window.innerHeight * 0.28)));
  }

  @HostListener('window:resize')
  onViewportResize(): void {
    this.inputMaxHeightPx = ChatbotComponent.computeInputMaxHeight();
  }

  /** `enabled && default resolvido` do GET /AI/profiles. false = chat de IA indisponível
   *  (kill-switch, catálogo ilegível, ou plano sem nenhum perfil alcançável). */
  profilesEnabled = false;
  /** true até o primeiro retorno (cache ou rede) do catálogo. Evita mostrar o aviso de
   *  "indisponível" e travar o envio num flash antes da resposta chegar. */
  profilesLoading = true;
  profileMenuOpen = false;
  profileOptions: AiProfileOption[] = [];
  /** Tipo de crédito que decidiu a vitrine (`GET /AI/profiles`); null em backend anterior aos tipos. */
  profilesCreditType: AiCreditType | null = null;
  /** Pedido ativo do usuário nesta conversa. null = nunca escolheu — o body do chat
   *  omite `profile` e o backend usa o gravado na sessão, senão o default do plano. */
  selectedProfile: string | null = null;
  /** Perfil que rodou de fato no último turno (evento SSE `profile.effective`) — única
   *  fonte do badge/indicador; nunca o que o front pediu. */
  effectiveProfile: string | null = null;

  /**
   * Default do plano, resolvido pelo backend em GET /AI/profiles. Guardado porque é
   * o que a UI mostra antes de qualquer escolha e antes do primeiro turno — sem ele,
   * o seletor aparecia sem texto e sem marcação até o usuário clicar em algo.
   */
  profilesDefault: string | null = null;

  renamingSessionId: string | null = null;
  renameValue = '';

  dislikeMessageId: string | null = null;
  dislikeReasons: string[] = [];
  dislikeComment = '';
  dislikeSending = false;
  feedbackLoadingId: string | null = null;

  sidebarVisible = window.innerWidth > 768;
  userScrolledUp = false;
  rateLimitCountdown = 0;

  // ─── Attach menu ────────────────────────────────────────────────────
  attachMenuOpen = false;
  attachMenuView: 'main' | 'skills' = 'main';
  chatSkills: AiSkill[] = [];
  emptyStarters: ChatStarter[] = [];
  skillsLoading = false;




  // ─── Skills Manager ─────────────────────────────────────────────────
  skillsManagerVisible = false;
  skillsManagerMode: 'list' | 'form' = 'list';
  customSkills: AiSkill[] = [];
  customSkillsLoading = false;
  skillFormMode: 'create' | 'edit' = 'create';
  selectedCustomSkill: AiSkill | null = null;
  skillForm: AiSkillUpsert = { name: '', type: 'custom', prompt: '', icon: 'bolt', isActive: true };
  skillFormSaving = false;

  readonly skillIconOptions = [
    { label: 'Bolt', value: 'bolt' },
    { label: 'Star', value: 'star' },
    { label: 'Lightbulb', value: 'lightbulb' },
    { label: 'Sparkles', value: 'sparkles' },
    { label: 'Microchip', value: 'microchip' },
    { label: 'Cloud', value: 'cloud' },
    { label: 'Code', value: 'code' },
    { label: 'Book', value: 'book' },
    { label: 'Comments', value: 'comments' },
    { label: 'Cog', value: 'cog' },
    { label: 'Graduation Cap', value: 'graduation-cap' },
    { label: 'Language', value: 'language' },
    { label: 'Pencil', value: 'pencil' },
    { label: 'Search', value: 'search' },
    { label: 'Shield', value: 'shield' },
    { label: 'Tag', value: 'tag' },
    { label: 'User', value: 'user' },
    { label: 'Wrench', value: 'wrench' },
  ];

  toggleAttachMenu(): void {
    this.attachMenuOpen = !this.attachMenuOpen;
    if (this.attachMenuOpen) this.actionsMode = null;
    this.attachMenuView = 'main';
  }

  closeAttachMenu(): void {
    this.attachMenuOpen = false;
    this.attachMenuView = 'main';
  }

  openSkillsManager(): void {
    this.attachMenuOpen = false;
    this.attachMenuView = 'main';
    this.skillsManagerMode = 'list';
    this.skillsManagerVisible = true;
    this.loadCustomSkillsList();
  }

  private loadCustomSkillsList(): void {
    const userId = this.authService.getUserInformations();
    if (!userId) return;
    this.customSkillsLoading = true;
    this.aiService.getCustomSkills(userId).subscribe({
      next: (skills) => { this.customSkills = skills; this.customSkillsLoading = false; },
      error: () => { this.customSkillsLoading = false; },
    });
  }

  openCreateSkill(): void {
    this.skillFormMode = 'create';
    this.selectedCustomSkill = null;
    this.skillForm = { name: '', type: 'custom', prompt: '', icon: 'bolt', isActive: true };
    this.skillsManagerMode = 'form';
  }

  openEditSkill(skill: AiSkill): void {
    this.skillFormMode = 'edit';
    this.selectedCustomSkill = skill;
    this.skillForm = { name: skill.name, type: skill.type, prompt: skill.prompt, icon: skill.icon, isActive: skill.isActive ?? true };
    this.skillsManagerMode = 'form';
  }

  saveSkill(): void {
    if (!this.skillForm.name?.trim() || !this.skillForm.prompt?.trim()) return;
    const userId = this.authService.getUserInformations();
    if (!userId) return;
    this.skillFormSaving = true;

    const onSuccess = () => {
      this.skillFormSaving = false;
      this.skillsManagerMode = 'list';
      this.loadCustomSkillsList();
      this.loadSkills();
    };
    const onError = () => { this.skillFormSaving = false; };

    if (this.skillFormMode === 'edit' && this.selectedCustomSkill) {
      this.aiService.updateCustomSkill(userId, this.selectedCustomSkill.id, this.skillForm)
        .subscribe({ next: onSuccess, error: onError });
    } else {
      this.aiService.createCustomSkill(userId, this.skillForm)
        .subscribe({ next: onSuccess, error: onError });
    }
  }

  confirmDeleteCustomSkill(skill: AiSkill): void {
    this.confirmationService.confirm({
      message: this.translocoService.translate('chatbot.skillsManager.deleteConfirm', { name: skill.name }),
      header: this.translocoService.translate('chatbot.skillsManager.deleteHeader'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translocoService.translate('common.buttons.yes'),
      rejectLabel: this.translocoService.translate('common.buttons.no'),
      accept: () => {
        const userId = this.authService.getUserInformations();
        if (!userId) return;
        this.aiService.deleteCustomSkill(userId, skill.id).subscribe({
          next: () => {
            this.customSkills = this.customSkills.filter(s => s.id !== skill.id);
            this.loadSkills();
          },
        });
      },
    });
  }

  // ─── Menu de ações "/" ──────────────────────────────────────────────
  actionsMode: 'button' | 'typed' | null = null;
  actionsFilter = '';
  actionItems: ChatAction[] = [];
  actionIndex = 0;
  actionsEmptyKey = 'chatbot.actions.empty';
  actionsLoading = false;
  @ViewChild('actionsFilterEl') actionsFilterEl?: ElementRef<HTMLInputElement>;
  @ViewChild('actionsListEl') actionsListEl?: ElementRef<HTMLElement>;

  // ─── Upload dialog ──────────────────────────────────────────────────
  private nextMessageIsSystem = false;
  uploadDialogVisible = false;
  uploadDialogTab: 'browse' | 'upload' = 'browse';
  // Browse existing files
  existingFiles: VcuFileItem[] = [];
  existingFilesLoading = false;
  private existingFilesLoaded = false;
  currentBrowsePath = '';
  // Upload new file
  chatUploadFiles: File[] = [];
  canRetry = false;
  private rateLimitTimer: ReturnType<typeof setInterval> | null = null;
  private lastSentRequest: AiChatRequest | null = null;
  private streamAbortController: AbortController | null = null;
  /** Frame pendente que agrupa os `content` de um mesmo instante num só redesenho (4.2). */
  private streamingRenderFrame: number | null = null;
  /** Marca cada resposta: eventos, push e finally de uma resposta antiga não mexem na atual. */
  private streamSeq = 0;

  // ─── Pending attachments (chips) ──────────────────────────────────
  pendingAttachments: ChatAttachment[] = [];
  private attachmentIdSeq = 0;

  toggleSidebar(): void {
    this.sidebarVisible = !this.sidebarVisible;
  }

  stopStreaming(): void {
    this.streamAbortController?.abort();
  }

  onChatScroll(event: Event): void {
    const el = event.target as HTMLElement;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    this.userScrolledUp = distanceFromBottom > 60;
  }

  scrollToBottomForce(): void {
    this.userScrolledUp = false;
    this.scrollToBottom();
  }

  trackBySessionId(_: number, s: AiSession): string { return s.session_id; }
  trackByMessageId(_: number, m: AiMessage): string { return m.message_id; }
  trackByIndex(i: number): number { return i; }
  trackByToolCallId(i: number, tc: { tool_call_id: string }): string { return tc.tool_call_id ?? i; }
  trackByAttachmentId(_: number, a: ChatAttachment): string { return a.id; }
  trackByStarterId(_: number, s: ChatStarter): string { return s.id; }
  trackByProfileKey(_: number, p: AiProfileOption): string { return p.key; }

  removePendingAttachment(id: string): void {
    this.pendingAttachments = this.pendingAttachments.filter(a => a.id !== id);
  }

  get hasReadyAttachment(): boolean {
    return this.pendingAttachments.some(a => a.status === 'ready');
  }

  get hasUploadingAttachment(): boolean {
    return this.pendingAttachments.some(a => a.status === 'uploading');
  }

  // ─── Upload dialog ──────────────────────────────────────────────────

  openChatUploadDialog(): void {
    // Guarda também aqui, e não só nos dois botões: no fix da compra de tokens desta
    // semana o buraco que sobrou foi exatamente um caminho programático que ninguém
    // lembrou de gatear. Qualquer chamador novo já nasce coberto.
    if (this.attachLocked) return;
    this.chatUploadFiles = [];
    this.uploadDialogTab = 'browse';
    this.currentBrowsePath = '';
    this.uploadDialogVisible = true;
    this.loadExistingFiles();
  }

  private loadExistingFiles(): void {
    const userID = this.authService.getUserInformations();
    if (!userID) return;
    this.existingFilesLoading = true;
    this.vcuService.getUserFiles(userID).subscribe({
      next: (res) => {
        this.existingFiles = res?.listItens ?? [];
        this.existingFilesLoaded = true;
        this.existingFilesLoading = false;
        if (this.actionsMode === 'typed') this.refreshActions();
        this.cdr.detectChanges();
      },
      error: () => {
        this.existingFilesLoading = false;
        this.actionsLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  isSkillMessage(content: string): boolean {
    return typeof content === 'string' && content.startsWith('skill:');
  }

  getSkillDisplayName(content: string): string {
    const rest = content.slice(6);
    const colonIdx = rest.indexOf(':');
    if (colonIdx < 0) return 'Skill habilitada';
    const skillId = rest.slice(0, colonIdx).trim();
    const skill = this.chatSkills.find(s => s.id === skillId);
    return skill ? `Skill: ${skill.name} ativada` : 'Skill habilitada';
  }

  getSkillIcon(content: string): string {
    const rest = content.slice(6);
    const colonIdx = rest.indexOf(':');
    if (colonIdx < 0) return 'bolt';
    const skillId = rest.slice(0, colonIdx).trim();
    return this.chatSkills.find(s => s.id === skillId)?.icon ?? 'bolt';
  }

  selectSkill(skill: AiSkill): void {
    if (!this.canDispatchNow) return;
    const content = `skill:${skill.id}: ${skill.prompt}`;
    this.attachMenuOpen = false;
    this.attachMenuView = 'main';
    this.userInput = content;
    this.nextMessageIsSystem = true;
    this.cdr.detectChanges();
    this.sendMessage();
  }

  get isCommandText(): boolean {
    return this.userInput.trimStart().startsWith('/');
  }

  get canDispatchNow(): boolean {
    return !this.hasUploadingAttachment && !this.isStreaming && this.rateLimitCountdown <= 0 && this.profilesEnabled;
  }

  trackByActionKey(_: number, action: ChatAction): string {
    return action.key;
  }

  private buildActions(typed: boolean): ChatAction[] {
    const t = (key: string, params?: object) => this.translocoService.translate(key, params);
    const items: ChatAction[] = [
      { key: 'new', section: 'conversation', label: t('chatbot.newChat'), disabled: this.isStreaming, run: () => this.newChat() },
      {
        key: 'files', section: 'conversation', label: t('chatbot.attachMenu.sendFiles'),
        note: this.attachLocked ? t('chatbot.paidPlanTag') : undefined,
        disabled: this.attachLocked, run: () => this.openChatUploadDialog(),
      },
      ...this.chatSkills.map(skill => ({
        key: `skill:${skill.id}`, section: 'agents' as const, label: skill.name,
        disabled: !this.canDispatchNow, run: () => this.selectSkill(skill),
      })),
      { key: 'manage', section: 'agents', label: t('chatbot.skillsManager.manage'), run: () => this.openSkillsManager() },
    ];
    if (this.profileSelectorVisible) {
      for (const p of this.profileOptions) {
        items.push({
          key: `profile:${p.key}`, section: 'model',
          label: t('chatbot.actions.profile', { name: this.profileDisplayName(p) }),
          note: p.available ? this.profileDescription(p) : t(this.profileLockedKey(p), { time: this.freeTankRefillTime }),
          checked: p.key === this.displayedProfileKey,
          run: () => this.pickProfile(p),
        });
      }
    }
    if (this.isTraceAdmin) {
      const debugLabel = t(this.traceModeActive ? 'chatbot.atDebug.deactivate' : 'chatbot.atDebug.activate');
      items.push({
        key: 'debug', section: 'tools',
        label: typed ? '/debug' : debugLabel, note: typed ? debugLabel : undefined, mono: typed,
        run: () => this.toggleTraceMode(),
      });
    }
    if (typed) {
      items.push(
        { key: 'cmd:file', section: 'conversation', label: '/file', note: t('chatbot.actions.cmd.file'), mono: true, run: () => this.completeCommand('file') },
        { key: 'cmd:skill', section: 'agents', label: '/skill', note: t('chatbot.actions.cmd.skill'), mono: true, run: () => this.completeCommand('skill') },
      );
      items.sort((a, b) => SECTION_ORDER.indexOf(a.section!) - SECTION_ORDER.indexOf(b.section!));
    }
    if (this.isAdminUser && this.currentSessionId) {
      items.push({
        key: 'export', section: 'tools', label: t('chatbot.exportTooltip'),
        disabled: this.isExportingConversation, run: () => this.downloadConversationJson(),
      });
    }
    return items;
  }

  private refreshActions(): void {
    this.actionsEmptyKey = 'chatbot.actions.empty';
    this.actionsLoading = false;
    const typed = this.actionsMode === 'typed';
    const child = typed ? this.actionsFilter.match(/^(file|skill)\s+(.*)$/i) : null;
    let preferred = -1;
    if (child) {
      this.actionItems = child[1].toLowerCase() === 'file' ? this.fileCommandItems(child[2]) : this.skillCommandItems(child[2]);
    } else {
      const q = foldText(this.actionsFilter);
      this.actionItems = this.buildActions(typed).filter(a =>
        !q || foldText(`${a.label} ${a.note ?? ''} ${a.section ? this.translocoService.translate('chatbot.actions.section.' + a.section) : ''}`).includes(q));
      if (typed && q) preferred = this.actionItems.findIndex(a => a.mono && !a.disabled && foldText(a.label).startsWith(`/${q}`));
    }
    this.actionIndex = preferred >= 0 ? preferred : Math.max(this.actionItems.findIndex(a => !a.disabled), 0);
  }

  private fileCommandItems(search: string): ChatAction[] {
    if (!this.existingFilesLoaded && !this.existingFilesLoading) this.loadExistingFiles();
    this.actionsLoading = this.existingFilesLoading;
    this.actionsEmptyKey = 'chatbot.atFile.noResults';
    const q = foldText(search);
    return this.existingFiles
      .filter(f => !q || foldText(`${f.fileName || ''} ${this.extractBrowseFolder(f.pathFile)}`).includes(q))
      .slice(0, 8)
      .map((f, i) => {
        const folder = this.extractBrowseFolder(f.pathFile);
        const badge = this.translocoService.translate((f.type || '') === 'map' ? 'chatbot.atFile.badgeMap' : 'chatbot.atFile.badgeLog');
        return {
          key: `file:${f.id}:${i}`, label: f.fileName || 'arquivo',
          note: [folder, badge].filter(Boolean).join(' · '), run: () => this.attachCloudFile(f),
        };
      });
  }

  private skillCommandItems(search: string): ChatAction[] {
    this.actionsEmptyKey = 'chatbot.atSpec.noResults';
    const q = foldText(search);
    return this.chatSkills
      .filter(s => !q || foldText(s.name).includes(q))
      .slice(0, 8)
      .map(skill => ({
        key: `cmd-skill:${skill.id}`, label: skill.name,
        disabled: !this.canDispatchNow, run: () => this.selectSkill(skill),
      }));
  }

  private completeCommand(name: 'file' | 'skill'): void {
    this.userInput = `/${name} `;
    this.openActionsMenu('typed', `${name} `);
  }

  private openActionsMenu(mode: 'button' | 'typed', filter: string): void {
    this.attachMenuOpen = false;
    this.attachMenuView = 'main';
    this.profileMenuOpen = false;
    this.actionsMode = mode;
    this.actionsFilter = filter;
    this.refreshActions();
    if (mode === 'button') setTimeout(() => this.actionsFilterEl?.nativeElement.focus());
  }

  toggleActionsMenu(): void {
    if (this.actionsMode === 'button') {
      this.closeActionsMenu(true);
    } else {
      this.openActionsMenu('button', '');
    }
  }

  closeActionsMenu(focusInput = false): void {
    this.actionsMode = null;
    if (focusInput) this.chatInputEl?.nativeElement.focus();
  }

  onActionsFilter(value: string): void {
    this.actionsFilter = value;
    this.refreshActions();
  }

  onActionsKeydown(event: KeyboardEvent): void {
    if (event.isComposing) return;
    const typed = this.actionsMode === 'typed';
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const next = this.actionIndex + (event.key === 'ArrowDown' ? 1 : -1);
      this.actionIndex = Math.max(0, Math.min(next, this.actionItems.length - 1));
      setTimeout(() => this.actionsListEl?.nativeElement.querySelector('[aria-selected="true"]')?.scrollIntoView?.({ block: 'nearest' }));
    } else if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.runAction(this.actionItems[this.actionIndex]);
    } else if (event.key === 'Tab' && typed && !event.shiftKey) {
      const item = this.actionItems[this.actionIndex];
      if (!item || item.disabled) return;
      event.preventDefault();
      this.runAction(item);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.closeActionsMenu(!typed);
    }
  }

  runAction(action?: ChatAction): void {
    if (!action || action.disabled) return;
    const typed = this.actionsMode === 'typed';
    this.actionsMode = null;
    if (typed) this.userInput = '';
    action.run();
    if (!typed) this.chatInputEl?.nativeElement.focus();
  }

  onUserInputChange(): void {
    const commandMatch = this.userInput.trimStart().match(/^\/([^\n]*)$/);
    if (commandMatch) {
      this.openActionsMenu('typed', commandMatch[1]);
      return;
    }
    this.actionsMode = null;
  }

  private attachCloudFile(file: VcuFileItem): void {
    this.pendingAttachments = [...this.pendingAttachments, {
      id: `att-${this.attachmentIdSeq++}`,
      fileId: file.id,
      fileName: file.fileName || 'arquivo',
      type: detectAttachmentType(file.fileName, file.type),
      source: 'mention',
      status: 'ready',
    }];
    this.cdr.detectChanges();
  }

  private toggleTraceMode(): void {
    const wasActive = this.traceModeActive;
    if (wasActive) {
      sessionStorage.removeItem('ai_trace');
    } else {
      sessionStorage.setItem('ai_trace', '1');
    }
    this.messages.push({
      message_id: 'temp-trace-' + Date.now(),
      role: 'assistant',
      content: this.translocoService.translate(wasActive ? 'chatbot.traceModeOff' : 'chatbot.traceModeOn'),
      content_format: 'plain',
      meta: null,
      timestamp: new Date().toISOString(),
      reasoning_content: null,
      feedback_rating: null,
      dislike_reasons: null,
      is_guardrail_blocked: null,
    });
    this.cdr.detectChanges();
    this.scrollToBottom();
  }

  onInputBlur(): void {
    setTimeout(() => {
      if (this.actionsMode === 'typed') this.actionsMode = null;
      this.cdr.detectChanges();
    }, 150);
  }

  private normalizeBrowsePath(pathFile?: string | null): string {
    if (!pathFile) return '';
    return pathFile
      .replace(/^[A-Za-z]:[\\/]/, '')
      .replace(/^[\\/]+/, '')
      .replace(/\\/g, '/')
      .replace(/^FuelTech Vision\//i, '');
  }

  extractBrowseFolder(pathFile?: string | null): string {
    const normalized = this.normalizeBrowsePath(pathFile);
    const lastSlash = normalized.lastIndexOf('/');
    return lastSlash >= 0 ? normalized.slice(0, lastSlash) : '';
  }

  getBrowseFolders(): Array<{ name: string; path: string }> {
    const set = new Set<string>();
    for (const f of this.existingFiles) {
      const folder = this.extractBrowseFolder(f.pathFile);
      if (!folder) continue;
      if (this.currentBrowsePath === '') {
        set.add(folder.split('/')[0]);
      } else if (folder === this.currentBrowsePath || folder.startsWith(this.currentBrowsePath + '/')) {
        const rest = folder.slice(this.currentBrowsePath.length).replace(/^\//, '');
        const nextSeg = rest.split('/')[0];
        if (nextSeg) set.add(this.currentBrowsePath + '/' + nextSeg);
      }
    }
    return Array.from(set).sort().map(path => ({ name: path.split('/').pop()!, path }));
  }

  getBrowseFiles(): VcuFileItem[] {
    return this.existingFiles.filter(
      f => this.extractBrowseFolder(f.pathFile) === this.currentBrowsePath,
    );
  }

  getBrowseBreadcrumb(): string[] {
    if (!this.currentBrowsePath) return [];
    return this.currentBrowsePath.split('/');
  }

  navigateBrowse(path: string): void {
    this.currentBrowsePath = path;
  }

  navigateBrowseUp(): void {
    const parts = this.currentBrowsePath.split('/');
    parts.pop();
    this.currentBrowsePath = parts.join('/');
  }

  navigateBrowseRoot(): void {
    this.currentBrowsePath = '';
  }

  selectExistingFile(file: VcuFileItem): void {
    const normalized = this.normalizeBrowsePath(file.pathFile);
    const name = file.fileName
      || (normalized ? normalized.split('/').pop() : null)
      || 'arquivo';
    this.uploadDialogVisible = false;
    this.pendingAttachments = [...this.pendingAttachments, {
      id: `att-${this.attachmentIdSeq++}`,
      fileId: file.id,
      fileName: name,
      type: detectAttachmentType(name, file.type),
      source: 'existing',
      status: 'ready',
    }];
    this.cdr.detectChanges();
  }

  onChatUploadFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (!files.length) return;

    const accepted: File[] = [];
    let rejected = 0;
    for (const f of files) {
      if (!this.detectUploadFileType(f.name)) { rejected++; continue; }
      const isDup = this.chatUploadFiles.some(s => s.name === f.name && s.size === f.size);
      if (!isDup) accepted.push(f);
    }
    if (rejected) {
      this.messageService.add({
        key: 'notification', severity: 'error',
        summary: this.translocoService.translate('arquivos.invalidFileType'),
        detail: this.translocoService.translate('arquivos.invalidFileTypeDetail'),
      });
    }
    if (accepted.length) {
      this.chatUploadFiles = [...this.chatUploadFiles, ...accepted];
    }
  }

  removeChatUploadFile(index: number): void {
    this.chatUploadFiles = this.chatUploadFiles.filter((_, i) => i !== index);
  }

  detectUploadFileType(fileName: string): 'map' | 'log' | null {
    const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
    if (ext === 'ftm' || ext === 'ftmap') return 'map';
    if (ext === 'ftml' || ext === 'ftlog') return 'log';
    return null;
  }

  formatUploadBytes(bytes: number): string {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    if (mb < 1024) return `${mb.toFixed(1)} MB`;
    return `${(mb / 1024).toFixed(2)} GB`;
  }

  get canStartChatUpload(): boolean {
    return this.chatUploadFiles.length > 0;
  }

  async startChatUpload(): Promise<void> {
    if (!this.canStartChatUpload) return;
    const userID = this.authService.getUserInformations();
    if (!userID) return;

    const files = this.chatUploadFiles;
    this.uploadDialogVisible = false;
    this.chatUploadFiles = [];
    await this.uploadAsAttachments(userID, files);
  }

  isChatDragOver = false;
  private chatDragCount = 0;

  private static hasFiles(e: DragEvent): boolean {
    return !!e.dataTransfer?.types?.includes('Files');
  }

  private get canDropAttach(): boolean {
    return this.profilesEnabled && !this.uploadDialogVisible;
  }

  @HostListener('document:dragenter', ['$event'])
  onDocDragEnter(e: DragEvent): void {
    if (!ChatbotComponent.hasFiles(e) || !this.canDropAttach) return;
    this.chatDragCount++;
    this.isChatDragOver = true;
  }

  @HostListener('document:dragleave', ['$event'])
  onDocDragLeave(e?: DragEvent): void {
    // WebKit sempre manda relatedTarget nulo no dragleave; só vale como "saiu da janela" se o ponteiro estiver fora do viewport.
    const leftWindow = !!e && e.relatedTarget === null
      && (e.clientX <= 0 || e.clientY <= 0 || e.clientX >= window.innerWidth || e.clientY >= window.innerHeight);
    this.chatDragCount = leftWindow ? 0 : Math.max(0, this.chatDragCount - 1);
    if (this.chatDragCount === 0) this.isChatDragOver = false;
  }

  @HostListener('document:dragover', ['$event'])
  onDocDragOver(e: DragEvent): void {
    if (ChatbotComponent.hasFiles(e)) e.preventDefault();
  }

  @HostListener('document:drop', ['$event'])
  async onDocDrop(e: DragEvent): Promise<void> {
    if (!ChatbotComponent.hasFiles(e)) return;
    e.preventDefault();
    this.chatDragCount = 0;
    this.isChatDragOver = false;
    if (!this.canDropAttach || this.attachLocked) return;
    const userID = this.authService.getUserInformations();
    if (!userID) return;

    const files = Array.from(e.dataTransfer?.files ?? []);
    const accepted = files.filter(f => this.detectUploadFileType(f.name));
    if (accepted.length < files.length) {
      this.messageService.add({
        key: 'notification', severity: 'error',
        summary: this.translocoService.translate('arquivos.invalidFileType'),
        detail: this.translocoService.translate('arquivos.invalidFileTypeDetail'),
      });
    }
    if (accepted.length) await this.uploadAsAttachments(userID, accepted);
  }

  private async uploadAsAttachments(userID: string, files: File[]): Promise<void> {
    const newAttachments: ChatAttachment[] = files.map(file => ({
      id: `att-${this.attachmentIdSeq++}`,
      fileName: file.name,
      type: this.detectUploadFileType(file.name) ?? 'map',
      source: 'upload',
      status: 'uploading',
      progress: 0,
    }));
    this.pendingAttachments = [...this.pendingAttachments, ...newAttachments];
    this.cdr.detectChanges();

    let storageLimitReached = false;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const attachmentId = newAttachments[i].id;
      if (!this.pendingAttachments.some(a => a.id === attachmentId)) continue;
      const type = this.detectUploadFileType(file.name);
      if (!type) {
        this.setAttachmentStatus(attachmentId, 'error');
        continue;
      }
      try {
        const fileId = await this.fileService.uploadUserFile(userID, type, file, (percent) => {
          this.setAttachmentProgress(attachmentId, percent);
        });
        const attachment = this.pendingAttachments.find(a => a.id === attachmentId);
        if (attachment && fileId) attachment.fileId = fileId;
        this.setAttachmentStatus(attachmentId, 'ready');
        this.existingFilesLoaded = false;
      } catch (err: any) {
        // Recusa permanente deste arquivo (ACC-006/007) também vem em 403, mas não é falta de
        // espaço: marca só ele e segue com o resto do lote.
        const rejectionKey = uploadRejectionKey(err);
        if (rejectionKey) {
          this.setAttachmentStatus(attachmentId, 'error');
          this.messageService.add({
            key: 'notification', severity: 'error',
            summary: this.translocoService.translate('common.labels.error'),
            detail: this.translocoService.translate(rejectionKey, { fileName: file.name }),
          });
          continue;
        }
        if (err?.status === 403 || err?.error?.code === 'ACC-003') {
          storageLimitReached = true;
          for (let j = i; j < files.length; j++) this.setAttachmentStatus(newAttachments[j].id, 'error');
          break;
        }
        this.setAttachmentStatus(attachmentId, 'error');
      }
    }

    if (storageLimitReached) {
      this.messageService.add({
        key: 'notification', severity: 'error',
        summary: this.translocoService.translate('messages.error.uploadFailed'),
        detail: this.translocoService.translate('messages.error.maxStorageReached'),
      });
    }
  }

  private setAttachmentProgress(id: string, progress: number): void {
    const attachment = this.pendingAttachments.find(a => a.id === id);
    if (!attachment) return; // user removed the chip while the upload was in flight
    attachment.progress = progress;
    this.cdr.detectChanges();
  }

  private setAttachmentStatus(id: string, status: ChatAttachment['status']): void {
    const attachment = this.pendingAttachments.find(a => a.id === id);
    if (!attachment) return; // user removed the chip while the upload was in flight
    attachment.status = status;
    this.cdr.detectChanges();
  }

  readonly dislikeReasonOptions = [
    { id: 'incorrect_or_incomplete', label: 'Incorreta ou incompleta' },
    { id: 'not_what_i_asked', label: 'Não respondeu o que perguntei' },
    { id: 'slow_or_errors', label: 'Lento ou com erros' },
    { id: 'tone_or_style', label: 'Tom ou estilo inadequado' },
    { id: 'security_or_legal', label: 'Problema de segurança' },
    { id: 'other', label: 'Outro' },
  ];

  constructor(
    private aiService: AiService,
    private accountService: AccountService,
    private authService: AuthService,
    private fileService: FileService,
    private vcuService: VcuService,
    private planFeatureService: PlanFeatureService,
    private translocoService: TranslocoService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private sanitizer: DomSanitizer,
    private titleService: Title,
    private planModalService: PlanModalService,
    private router: Router,
  ) {}

  /**
   * Cache do HTML renderizado por conteúdo. Sem ele, cada change detection
   * (inclusive o disparado no `mouseup`) recriaria o `SafeHtml` e re-injetaria o
   * `innerHTML`, recriando o DOM e **apagando a seleção de texto** do usuário.
   * Retornar a mesma referência mantém o `innerHTML` intocado.
   */
  private renderCache = new Map<string, SafeHtml>();
  private static readonly RENDER_CACHE_MAX = 300;

  renderContent(content: string, format: string | null, cache = true): SafeHtml {
    if (!content) return '';
    if (format !== 'markdown') return content;

    const cached = this.renderCache.get(content);
    if (cached !== undefined) return cached;

    const safe = this.sanitizer.bypassSecurityTrustHtml(marked.parse(content) as string);
    // O parcial do streaming muda a cada frame: nunca acerta e expulsaria o histórico.
    if (!cache) return safe;
    // Cache limitado: evita crescimento infinito.
    if (this.renderCache.size >= ChatbotComponent.RENDER_CACHE_MAX) {
      const oldest = this.renderCache.keys().next().value;
      if (oldest !== undefined) this.renderCache.delete(oldest);
    }
    this.renderCache.set(content, safe);
    return safe;
  }

  private loadSkills(): void {
    const userId = this.authService.getUserInformations();
    if (!userId) return;
    this.skillsLoading = true;
    forkJoin([
      this.aiService.getUserSkills(userId).pipe(catchError(() => of([]))),
      this.aiService.getCustomSkills(userId).pipe(catchError(() => of([]))),
    ]).subscribe(([global, custom]) => {
      this.chatSkills = [...global, ...custom];
      this.skillsLoading = false;
      this.buildEmptyStarters();
    });
  }

  /** Pontos de partida da tela vazia: as skills que a API devolveu (no máximo
   *  duas, para a tela não virar um menu) mais anexar arquivo, que é o outro
   *  caminho de entrada real. Recalculado quando as skills chegam. */
  private buildEmptyStarters(): void {
    this.emptyStarters = [
      ...this.chatSkills.slice(0, 2).map(skill => ({
        id: `skill:${skill.id}`,
        icon: skill.icon || 'bolt',
        label: skill.name,
        skill: true,
        run: () => this.selectSkill(skill),
      })),
      {
        id: 'attach',
        icon: 'paperclip',
        labelKey: 'chatbot.empty.starterAttach',
        run: () => this.openChatUploadDialog(),
        locked: this.attachLocked,
      },
    ];
  }

  runStarter(starter: ChatStarter): void {
    // Guarda no comportamento, não só no `disabled` do template: teclado e leitor de
    // tela alcançam o botão por outros caminhos.
    if (starter.locked || (starter.skill && !this.canDispatchNow)) return;
    starter.run();
  }

  ngOnInit(): void {
    document.body.classList.add('chatbot-page-active');
    // Monta já com o starter de anexo; loadSkills() reconstrói acrescentando as
    // skills quando chegarem. Sem isto a tela vazia ficaria sem nenhum caminho
    // de entrada enquanto carrega, ou de vez se loadSkills() sair cedo.
    this.buildEmptyStarters();
    this.setTitle();
    this.loadSessions();
    this.loadAccountInfo();
    this.loadProfileCatalog();
    this.loadSkills();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    document.body.classList.remove('chatbot-page-active');
    if (this.rateLimitTimer) {
      clearInterval(this.rateLimitTimer);
    }
  }

  setTitle() {
    this.translocoService
      .selectTranslate("title.ftassistant")
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => this.titleService.setTitle(value));
  }

  private loadAccountInfo(): void {
    const userId = this.authService.getUserInformations();
    this.accountService.getAccount(userId).subscribe({
      next: (account) => {
        this.isAdminUser = account.accessLevel === 2;
        this.planFeatureService.load(account.planInformation);
      },
      error: () => {
        this.isAdminUser = false;
      },
    });
  }

  /** Mostra o seletor só quando o catálogo está habilitado e tem itens pra listar. */
  get profileSelectorVisible(): boolean {
    return this.profilesEnabled && this.profileOptions.length > 0;
  }

  /**
   * Fonte ÚNICA do perfil que a UI exibe — badge e marcação do menu. Não confundir
   * com `selectedProfile`, que existe só para decidir se o body manda `profile` e
   * portanto precisa continuar nulo enquanto o usuário não escolher nada.
   *
   * Precedência: o que de fato rodou no último turno (inclusive downgrade, que é a
   * verdade) → a escolha pendente do usuário → o default do plano.
   */
  get displayedProfileKey(): string | null {
    return this.effectiveProfile ?? this.selectedProfile ?? this.profilesDefault;
  }

  //nome/descrição i18n por key; sem tradução cai no texto da vitrine (perfil novo, front antigo)
  get currentProfileName(): string {
    const key = this.displayedProfileKey;
    const current = this.profileOptions.find(p => p.key === key);
    if (current) return this.profileDisplayName(current);
    return key ?? '';
  }

  pickProfile(profile: AiProfileOption): void {
    this.profileMenuOpen = false;
    if (!profile.available) {
      this.handleLockedProfileCta(profile.locked_reason);
      return;
    }
    this.onProfileChange(profile.key);
  }

  //CTA da vitrine: no_balance e credit_type_required mandam pra tela de tanques (recarga, ou
  //ver os tipos de crédito — a API não diz qual tipo libera o perfil); o resto (plan_required
  //e motivo novo/desconhecido) manda pro upsell de plano
  private handleLockedProfileCta(reason: string | null): void {
    if (reason === 'no_balance' || reason === 'credit_type_required') {
      this.navigateToTopUp();
    } else {
      this.openPlansModal();
    }
  }

  navigateToTopUp(): void {
    this.router.navigate(['/profile/ai']);
  }

  profileDisplayName(p: AiProfileOption): string {
    const key = 'chatbot.profileNames.' + p.key;
    const t = this.translocoService.translate(key);
    return t === key ? p.name : t;
  }

  profileDescription(p: AiProfileOption): string {
    const key = 'chatbot.profileDescriptions.' + p.key;
    const t = this.translocoService.translate(key);
    return t === key ? (p.description || '') : t;
  }

  //troca de perfil: confirma antes de aplicar (perfil é o "modo de condução" — muda custo e
  //comportamento pro resto da conversa) e registra aviso inline só no accept
  onProfileChange(newKey: string): void {
    const current = this.selectedProfile ?? this.effectiveProfile;
    if (!newKey || newKey === current) return;
    const option = this.profileOptions.find(p => p.key === newKey);
    const name = option ? this.profileDisplayName(option) : newKey;
    this.confirmationService.confirm({
      message: this.translocoService.translate('chatbot.profileSwitchConfirm', { name }),
      header: this.translocoService.translate('chatbot.profileSwitchHeader'),
      icon: 'pi pi-sync',
      acceptLabel: this.translocoService.translate('chatbot.deleteYes'),
      rejectLabel: this.translocoService.translate('chatbot.deleteNo'),
      accept: () => {
        this.selectedProfile = newKey;
        // O effective descreve o turno ANTERIOR; após uma escolha nova ele não
        // representa mais o estado atual, senão o badge continuaria no perfil velho.
        this.effectiveProfile = null;
        this.messages.push({
          message_id: 'temp-profile-change-' + Date.now(),
          role: 'assistant',
          content: this.translocoService.translate('chatbot.profileSwitchNotice', { name }),
          content_format: 'plain',
          meta: null,
          timestamp: new Date().toISOString(),
          reasoning_content: null,
          feedback_rating: null,
          dislike_reasons: null,
          is_guardrail_blocked: null,
          event_type: 'profile_change',
        });
        this.scrollToBottom();
      },
    });
  }

  //abre o seletor e refaz o GET (vitrine pode ter mudado: saldo, plano, catálogo)
  toggleProfileMenu(): void {
    this.profileMenuOpen = !this.profileMenuOpen;
    if (this.profileMenuOpen) {
      this.actionsMode = null;
      this.loadProfileCatalog();
    }
  }

  /**
   * Reserva gratuita em uso, se houver. Serve só ao copy: o motivo `no_balance` da vitrine
   * diz "aguarde o próximo mês", o que é falso pra quem está na reserva — ele espera o
   * refill da janela. Carregada junto da vitrine porque muda pelos mesmos motivos.
   */
  private freeTank: AiFreeTank | null = null;

  /**
   * `null` enquanto o consumo não chegou (ou se a chamada falhou). Nesse caso o anexo
   * segue liberado: derrubar um recurso central de quem paga por causa de uma requisição
   * de metadados que falhou é pior do que deixar um Free tentar — o backend recusa.
   */
  private isPaid: boolean | null = null;

  /** Anexar log/mapa é recurso de plano pago. */
  get attachLocked(): boolean {
    return this.isPaid === false;
  }

  get isOnFreeTank(): boolean {
    return isFreeTankInUse(this.freeTank);
  }

  /** Horário do refill em HH:mm, no fuso do usuário. Vazio fora da reserva ou sem janela aberta. */
  get freeTankRefillTime(): string {
    return this.freeTank?.refill_at ? formatRefillTime(this.freeTank.refill_at) : '';
  }

  /**
   * Copy do perfil bloqueado. O motivo `no_balance` tem três leituras: fora da reserva o
   * usuário realmente espera o próximo mês; nela, espera o refill da janela — e antes da
   * primeira mensagem não há refill agendado pra citar. A frase do meio é a única errada
   * pros dois últimos casos, então cada um tem a sua.
   */
  profileLockedKey(p: AiProfileOption): string {
    if (p.locked_reason === 'credit_type_required') return creditTypeKey('chatbot.profileLockedCreditType', this.profilesCreditType);
    if (p.locked_reason !== 'no_balance') return 'chatbot.profileLockedPlanRequired';
    if (!this.isOnFreeTank) return 'chatbot.profileLockedNoBalance';
    return this.freeTankRefillTime
      ? 'chatbot.profileLockedNoBalanceReserve'
      : 'chatbot.profileLockedNoBalanceReserveIdle';
  }

  //vitrine do seletor; enabled=false ou default=null (kill-switch/sem seed/plano sem
  //perfil algum) esconde o seletor E desabilita o envio de mensagem
  private loadProfileCatalog(): void {
    //cache: seletor no primeiro paint; a resposta do servidor atualiza em seguida
    try {
      const cached = JSON.parse(sessionStorage.getItem('ai_profile_catalog') || 'null');
      if (cached) {
        this.profileOptions = cached.profiles ?? [];
        this.profilesDefault = cached.default ?? null;
        this.profilesCreditType = cached.credit_type ?? null;
        this.profilesEnabled = cached.enabled === true && cached.default != null;
        this.profilesLoading = false;
      }
    } catch { /* cache inválido: ignora */ }
    this.aiService.getProfiles().subscribe({
      next: (res) => {
        this.profileOptions = res.profiles ?? [];
        this.profilesDefault = res.default ?? null;
        this.profilesCreditType = res.credit_type ?? null;
        this.profilesEnabled = res.enabled === true && res.default != null;
        this.profilesLoading = false;
        sessionStorage.setItem('ai_profile_catalog', JSON.stringify({ enabled: res.enabled, default: res.default, credit_type: this.profilesCreditType, profiles: this.profileOptions }));
      },
      error: () => {
        this.profilesEnabled = false;
        this.profilesLoading = false;
      },
    });
    this.aiService.getUsage().subscribe({
      next: (usage) => {
        this.freeTank = usage.free_tank ?? null;
        this.isPaid = isPaidPlan(usage);
        this.buildEmptyStarters();   // o starter de anexo depende do plano
      },
      // Falhar aqui só custa a variante do copy — o seletor e o envio seguem normais.
      error: () => { this.freeTank = null; },
    });
  }

  // ─── Sessions ──────────────────────────────────────────────────────

  loadSessions(): void {
    this.sessionsLoading = true;
    this.aiService.getSessions(30).subscribe({
      next: (res) => {
        this.sessions = res.items;
        this.nextCursor = res.next_cursor;
        this.sessionsLoading = false;
      },
      error: () => {
        this.sessionsLoading = false;
      },
    });
  }

  loadMoreSessions(): void {
    if (!this.nextCursor) return;
    this.sessionsLoading = true;
    this.aiService.getSessions(30, this.nextCursor).subscribe({
      next: (res) => {
        this.sessions = [...this.sessions, ...res.items];
        this.nextCursor = res.next_cursor;
        this.sessionsLoading = false;
      },
      error: () => {
        this.sessionsLoading = false;
      },
    });
  }

  /** Normaliza os dois formatos de clarification num ClarificationData:
   *  o novo `{ intro, questions[] }` e o legado `{ question, options[] }`.
   *
   *  Existia so no caminho do SSE ao vivo; a restauracao do historico lia
   *  apenas o legado. Com o backend mandando o formato novo, voltar para uma
   *  sessao com pergunta em aberto montava uma caixa sem pergunta e sem opcao
   *  — e como clarificationData ficava preenchido, o campo de digitar travava
   *  e a conversa virava um beco sem saida.
   *
   *  Devolve null quando nao ha pergunta aproveitavel: melhor seguir sem
   *  clarify do que bloquear a sessao. */
  private toClarificationData(raw: any, fallbackId: string): ClarificationData | null {
    if (!raw) return null;

    if (Array.isArray(raw.questions)) {
      const questions = raw.questions.filter((q: any) => q && q.question);
      if (questions.length) return { intro: raw.intro ?? '', questions };
    }

    if (raw.question) {
      return {
        intro: raw.intro ?? '',
        questions: [{
          id: raw.id ?? fallbackId,
          question: raw.question,
          // O legado manda string; o novo, objeto.
          options: (raw.options ?? []).map((o: any) => (typeof o === 'string' ? { label: o } : o)),
          multiSelect: false,
          allowOther: true,
        }],
      };
    }

    return null;
  }

  private reconstructMessages(items: AiMessage[]): { messages: AiMessage[]; pendingClarification: ClarificationData | null } {
    const sorted = [...items].sort((a, b) => {
      if (a.timestamp !== b.timestamp) return a.timestamp < b.timestamp ? -1 : 1;
      if (a.role === 'user' && b.role !== 'user') return -1;
      if (a.role !== 'user' && b.role === 'user') return 1;
      return 0;
    });

    const display: AiMessage[] = [];
    const assistantMap: Record<string, AiMessage> = {};
    let pendingClarification: ClarificationData | null = null;
    let pendingClarificationId: string | null = null;

    // Pass 1: all types except 'thinking'
    for (const item of sorted) {
      const { event_type, role, message_id } = item;

      // Legacy (no event_type)
      if (!event_type) {
        if (role === 'user') {
          display.push(item);
        } else if (role === 'assistant' && item.content.trim()) {
          display.push(item);
          assistantMap[message_id] = item;
        }
        continue;
      }

      if (event_type === 'user_message') {
        display.push(item);
      } else if (event_type === 'assistant_message') {
        if (!item.content.trim()) continue;
        const msg: AiMessage = { ...item };
        display.push(msg);
        assistantMap[message_id] = msg;
      } else if (event_type === 'artifact') {
        try {
          const parsed = JSON.parse(item.content);
          const parent = item.parent_message_id ? assistantMap[item.parent_message_id] : null;
          let target: AiMessage;
          if (parent) {
            target = parent;
          } else {
            target = { ...item, content: '', content_format: 'plain' };
            display.push(target);
          }
          if (parsed.type === 'ftcf' || parsed.type === 'ftcf-canvas') {
            target.ftcfArtifact = parsed.payload as FtcfArtifact;
          } else if (parsed.type === 'ftcf-file') {
            target.ftcfFile = parsed.payload as FtcfFile;
          } else if (parsed.type === 'datalog-channels') {
            target.datalogChart = normalizeDatalogArtifact(parsed.payload);
          } else if (parsed.type === 'injection-map') {
            target.mapArtifacts = [...(target.mapArtifacts ?? []), parsed.payload];
          } else if (parsed.type === 'map-correction') {
            target.correctionArtifacts = [...(target.correctionArtifacts ?? []), parsed.payload];
          } else if (parsed.type === 'drag-run') {
            target.dragRuns = appendDragRun(target.dragRuns, parsed.payload);
          } else if (parsed.type === 'chart') {
            target.charts = [...(target.charts ?? []), parsed.payload];
          }
        } catch { /* ignore malformed */ }
      } else if (event_type === 'clarification') {
        try {
          const parsed = JSON.parse(item.content);
          const data = this.toClarificationData(parsed, message_id);
          const msg: AiMessage = {
            ...item,
            content: '',
            content_format: 'plain',
            // O historico mostra uma linha so; com varias perguntas, junta.
            clarification_question: (data?.questions ?? []).map(q => q.question).join(' · '),
            clarification_options: (data?.questions?.[0]?.options ?? []).map(o => o.label),
          };
          display.push(msg);
          if (item.status === 'pending' && data) {
            pendingClarification = data;
            pendingClarificationId = message_id;
          }
        } catch { /* ignore malformed */ }
      } else if (event_type === 'guardrail') {
        display.push({
          ...item,
          is_guardrail_blocked: true,
          content: this.translocoService.translate('chatbot.blocked'),
          content_format: 'plain',
        });
      }
      // 'thinking' e 'trace' → handled in pass 2
    }

    // Pass 2: attach thinking blocks and trace rows to parent assistant messages
    for (const item of sorted) {
      if (item.event_type === 'trace') {
        this.attachTraceRow(item, assistantMap);
        continue;
      }
      if (item.event_type !== 'thinking') continue;
      const parent = item.parent_message_id ? assistantMap[item.parent_message_id] : null;
      if (parent) {
        parent.reasoning_content = item.content;
      } else {
        display.push({
          ...item,
          content: '',
          content_format: 'plain',
          reasoning_content: item.content,
        });
      }
    }

    // So reabre a caixa se a pergunta for a ultima coisa da conversa. O backend
    // mantem o status 'pending' mesmo depois de a pessoa descartar as perguntas
    // e seguir com outra duvida; sem esta checagem, recarregar a sessao
    // ressuscitaria a caixa antiga e travaria o campo de novo.
    if (pendingClarificationId && display[display.length - 1]?.message_id !== pendingClarificationId) {
      pendingClarification = null;
    }

    return { messages: display, pendingClarification };
  }

  /**
   * Linha agregada do trace ADM (uma por turno, gravada só com o trace ligado): `content` é o
   * array de eventos do SSE. Sem resposta com texto para pendurar (turno sem conteúdo), o trace
   * fica de fora — anexar ao fim da lista o mostraria fora de ordem.
   */
  private attachTraceRow(item: AiMessage, assistantMap: Record<string, AiMessage>): void {
    const parent = item.parent_message_id ? assistantMap[item.parent_message_id] : null;
    if (!parent) return;
    try {
      const events = JSON.parse(item.content);
      if (Array.isArray(events)) parent.traceEvents = events as TraceEvent[];
    } catch { /* ignore malformed */ }
  }

  selectSession(session: AiSession): void {
    if (this.isStreaming) return;
    if (session.session_id !== this.currentSessionId) this.pendingAttachments = [];
    if (window.innerWidth <= 768) {
      this.sidebarVisible = false;
    }
    this.currentSessionId = session.session_id;
    this.messagesLoading = true;
    this.messages = [];
    this.streamingContent = '';
    this.clarificationData = null;
    this.clarificationAnswers = new Map();
    this.clarificationOtherMap = {};

    this.aiService.getSession(session.session_id).subscribe({
      next: (res) => {
        this.selectedProfile = res.session?.profile ?? null;
        this.effectiveProfile = null;
        const { messages, pendingClarification } = this.reconstructMessages(res.messages);
        this.messages = messages;
        if (pendingClarification) {
          this.clarificationData = pendingClarification;
          this.currentClarificationTab = 0;
        }
        this.messagesLoading = false;
        this.scrollToBottom();
      },
      error: () => {
        this.messagesLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translocoService.translate('chatbot.errorGeneric'),
        });
      },
    });
  }

  newChat(): void {
    if (this.isStreaming) return;
    this.pendingAttachments = [];
    this.currentSessionId = null;
    this.messages = [];
    this.streamingContent = '';
    this.userInput = '';
    this.selectedProfile = null;
    this.effectiveProfile = null;
    this.clarificationData = null;
    this.clarificationAnswers = new Map();
    this.clarificationOtherMap = {};
  }

  // ─── Rename / Delete ──────────────────────────────────────────────

  startRename(session: AiSession, event: Event): void {
    event.stopPropagation();
    this.renamingSessionId = session.session_id;
    this.renameValue = session.title;
  }

  confirmRename(session: AiSession): void {
    const title = this.renameValue.trim();
    if (!title) return;
    this.aiService.renameSession(session.session_id, title).subscribe({
      next: (res) => {
        session.title = res.session.title;
        this.renamingSessionId = null;
      },
      error: () => {
        this.renamingSessionId = null;
      },
    });
  }

  cancelRename(): void {
    this.renamingSessionId = null;
  }

  deleteSession(session: AiSession, event: Event): void {
    event.stopPropagation();
    this.confirmationService.confirm({
      message: this.translocoService.translate('chatbot.deleteConfirm'),
      acceptLabel: this.translocoService.translate('chatbot.deleteYes'),
      rejectLabel: this.translocoService.translate('chatbot.deleteNo'),
      accept: () => {
        this.aiService.deleteSession(session.session_id).subscribe({
          next: () => {
            this.sessions = this.sessions.filter(s => s.session_id !== session.session_id);
            if (this.currentSessionId === session.session_id) {
              this.newChat();
            }
          },
        });
      },
    });
  }

  // ─── Send Message (SSE) ───────────────────────────────────────────

  async sendMessage(): Promise<void> {
    const input = this.userInput.trim();
    const readyAttachments = this.pendingAttachments.filter(a => a.status === 'ready');
    const erroredAttachments = this.pendingAttachments.filter(a => a.status === 'error');

    if (this.isCommandText || !this.canDispatchNow || (!input && !readyAttachments.length)) return;

    this.profileMenuOpen = false;

    for (const attachment of erroredAttachments) {
      this.messageService.add({
        key: 'notification', severity: 'error',
        summary: this.translocoService.translate('chatbot.attachments.uploadError', { fileName: attachment.fileName }),
      });
    }

    const attachmentsHeader = this.translocoService.translate('chatbot.attachments.sentHeader');
    const composedMessage = composeMessageWithAttachments(input, readyAttachments, attachmentsHeader);

    const isSystem = this.nextMessageIsSystem;
    this.nextMessageIsSystem = false;
    this.messages.push({
      message_id: 'temp-user-' + Date.now(),
      role: 'user',
      content: input,
      content_format: 'plain',
      meta: null,
      timestamp: new Date().toISOString(),
      reasoning_content: null,
      feedback_rating: null,
      dislike_reasons: null,
      is_guardrail_blocked: null,
      is_system: isSystem || undefined,
      attachments: readyAttachments.length ? readyAttachments : undefined,
    });
    this.userInput = '';
    this.pendingAttachments = [];

    const logInfo = buildLogInfo(readyAttachments);
    const body: AiChatRequest = {
      message: composedMessage,
      session_id: this.currentSessionId ?? undefined,
      context: {
        app_info: {
          app_id: 'web_client',
          language: sessionStorage.getItem('language') ?? '',
          // Liga o streaming de tokens no servidor (content em pedaços, com `replace`).
          capabilities: ['content_replace'],
        },
        ...(logInfo ? { log_info: logInfo } : {}),
      },
    };
    // Só manda quando o usuário escolheu ativamente no seletor — omitido, o backend usa
    // o gravado na sessão, senão o default do plano.
    if (this.selectedProfile) {
      body.profile = this.selectedProfile;
    }
    if (this.traceModeActive) {
      body.trace = true;
    }

    await this.executeStreamingRequest(body, input || composedMessage);
  }

  retryLastMessage(): void {
    if (!this.lastSentRequest || this.isStreaming) return;
    this.canRetry = false;
    this.executeStreamingRequest(this.lastSentRequest, '');
  }

  private async executeStreamingRequest(body: AiChatRequest, userInputPreview: string): Promise<void> {
    const streamId = ++this.streamSeq;
    this.lastSentRequest = body;
    this.isStreaming = true;
    this.streamingContent = '';
    this.streamingGuardrailBlocked = false;
    this.streamingTraceEvents = [];
    this.streamingProgressSteps = [];
    this.userScrolledUp = false;
    this.streamAbortController = new AbortController();
    this.scrollToBottom();

    let assistantMessageId = '';
    let isGuardrailBlocked = false;

    try {
      await this.aiService.sendMessageSSE(
        body,
        this.currentSessionId,
        null,
        (event) => {
          if (streamId !== this.streamSeq) return; // evento de uma resposta que já não é a atual
          this.ngZone.run(() => {
            switch (event.event) {
              case 'session_created':
                this.currentSessionId = event.data.session_id;
                this.addSessionToList(event.data.session_id, userInputPreview || body.message);
                break;
              case 'profile': {
                const p = event.data as AiProfileEvent;
                // effective é a única fonte do que rodou — nunca o que o front pediu.
                this.effectiveProfile = p.effective ?? null;
                if (p.downgraded) {
                  const reason = p.reason === 'unknown_profile' ? 'not_available' : p.reason;
                  if (reason === 'access_limited') {
                    this.addErrorMessage('chatbot.profileDowngradedBalance', {}, false, true);
                  } else if (reason === 'credit_type_limited') {
                    // Fora da reserva: o perfil existe, mas não com o crédito em uso. A saída é a
                    // tela de tanques, não o upsell de plano.
                    this.addErrorMessage(creditTypeKey('chatbot.profileDowngradedCreditType', p.credit_type), {}, false, true);
                  } else {
                    this.addErrorMessage('chatbot.profileDowngradedPlan', {}, true, false);
                  }
                  // unknown_profile: nosso catálogo em cache está desatualizado, refaz o GET.
                  if (p.reason === 'unknown_profile') this.loadProfileCatalog();
                  this.cdr.detectChanges();
                }
                break;
              }
              case 'content':
                this.streamingContent = applyContentEvent(this.streamingContent, event.data, isGuardrailBlocked);
                this.scheduleStreamingRender();
                break;
              case 'thinking':
                this.thinkingContent += event.data.text;
                break;
              case 'tool_call':
                this.activeToolCalls.push({
                  tool_call_id: event.data.tool_call_id,
                  tool_name: event.data.tool_name,
                });
                this.cdr.detectChanges();
                this.scrollToBottom();
                break;
              case 'agent_handoff':
                this.streamingAgentHandoff = { from: event.data.from, to: event.data.to };
                this.cdr.detectChanges();
                break;
              case 'trace':
                this.streamingTraceEvents.push(event.data as TraceEvent);
                this.cdr.detectChanges();
                break;
              case 'progress': {
                const p = event.data as ProgressStep;
                if (!p?.step) break;
                // Upsert por `step` (start → spinner, done → ✓), reordenando:
                // concluídas no topo (ordem de conclusão), carregando sempre embaixo.
                this.upsertProgressStep(p);
                this.cdr.detectChanges();
                this.scrollToBottom();
                break;
              }
              case 'artifact':
                if (event.data.type === 'ftcf-canvas') {
                  this.streamingFtcfArtifact = event.data.payload as FtcfArtifact;
                } else if (event.data.type === 'ftcf-file') {
                  this.streamingFtcfFile = event.data.payload as FtcfFile;
                } else if (event.data.type === 'datalog-channels') {
                  this.streamingDatalogChart = normalizeDatalogArtifact(event.data.payload);
                } else if (event.data.type === 'injection-map') {
                  this.streamingMapArtifacts.push(event.data.payload as MapArtifactPayload);
                } else if (event.data.type === 'map-correction') {
                  this.streamingCorrectionArtifacts.push(event.data.payload as CorrectionArtifactPayload);
                } else if (event.data.type === 'drag-run') {
                  this.streamingDragRuns = appendDragRun(this.streamingDragRuns, event.data.payload);
                } else if (event.data.type === 'chart') {
                  this.streamingCharts = [...this.streamingCharts, event.data.payload];
                }
                break;
              case 'clarification':
                this.clarificationData = this.toClarificationData(event.data, 'q1');
                this.clarificationAnswers = new Map();
                this.clarificationOtherMap = {};
                this.currentClarificationTab = 0;
                this.isStreaming = false;
                this.cdr.detectChanges();
                this.scrollToBottom();
                break;
              case 'guardrail': {
                const g = event.data;
                if (g.blocked) {
                  isGuardrailBlocked = true;
                  this.streamingGuardrailBlocked = true;
                  if (g.code === AI_ERROR_CODES.GUARDRAIL_INPUT_BLOCKED) {
                    this.streamingContent = this.translocoService.translate('chatbot.guardrailInputBlocked');
                  } else if (g.code === AI_ERROR_CODES.GUARDRAIL_OUTPUT_BLOCKED) {
                    this.streamingContent = this.translocoService.translate('chatbot.guardrailOutputBlocked');
                  } else {
                    this.streamingContent = this.translocoService.translate('chatbot.blocked');
                  }
                  this.cdr.detectChanges();
                }
                break;
              }
              case 'done':
                assistantMessageId = event.data.message_id;
                this.lastSentRequest = null;
                // Fim do turno: finaliza qualquer etapa ainda em "start" (para os spinners).
                this.streamingProgressSteps = this.streamingProgressSteps.map((s) =>
                  s.status === 'done' ? s : { ...s, status: 'done' as const }
                );
                break;
              case 'error': {
                const sseErr = event.data as AiErrorResponse;
                if (sseErr.code === AI_ERROR_CODES.TIMEOUT) {
                  this.streamingContent = this.translocoService.translate('chatbot.timeout');
                } else {
                  this.streamingContent = sseErr.error ?? this.translocoService.translate('chatbot.errorGeneric');
                }
                this.cdr.detectChanges();
                break;
              }
            }
          });
        },
        this.streamAbortController.signal,
      );

      if (streamId !== this.streamSeq) return;
      this.ngZone.run(() => {
        const assistantMsg: AiMessage = {
          message_id: assistantMessageId || 'temp-bot-' + Date.now(),
          role: 'assistant',
          content: this.streamingContent,
          content_format: 'markdown',
          meta: null,
          timestamp: new Date().toISOString(),
          reasoning_content: this.thinkingContent || null,
          feedback_rating: null,
          dislike_reasons: null,
          is_guardrail_blocked: isGuardrailBlocked || null,
          ftcfArtifact: this.streamingFtcfArtifact ?? undefined,
          ftcfFile: this.streamingFtcfFile ?? undefined,
          datalogChart: this.streamingDatalogChart ?? undefined,
          dragRuns: this.streamingDragRuns.length ? [...this.streamingDragRuns] : undefined,
          charts: this.streamingCharts.length ? [...this.streamingCharts] : undefined,
          mapArtifacts: this.streamingMapArtifacts.length ? [...this.streamingMapArtifacts] : undefined,
          correctionArtifacts: this.streamingCorrectionArtifacts.length ? [...this.streamingCorrectionArtifacts] : undefined,
          traceEvents: this.streamingTraceEvents.length ? [...this.streamingTraceEvents] : undefined,
          progressSteps: this.streamingProgressSteps.length ? [...this.streamingProgressSteps] : undefined,
        };
        const hasTraceOnly = this.traceModeActive && this.streamingTraceEvents.length > 0;
        if (this.streamingContent || this.streamingFtcfArtifact || this.streamingFtcfFile || this.streamingDatalogChart || this.streamingDragRuns.length || this.streamingCharts.length || this.streamingMapArtifacts.length || this.streamingCorrectionArtifacts.length || hasTraceOnly) {
          this.messages.push(assistantMsg);
          this.updateSessionPreview(userInputPreview, this.streamingContent);
        }
      });
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        this.handleAiHttpError(err);
      }
    } finally {
      if (streamId === this.streamSeq) {
        this.ngZone.run(() => {
          if (this.streamingRenderFrame !== null) {
            cancelAnimationFrame(this.streamingRenderFrame);
            this.streamingRenderFrame = null;
          }
          this.isStreaming = false;
          this.streamingContent = '';
          this.streamingGuardrailBlocked = false;
          this.thinkingContent = '';
          this.activeToolCalls = [];
          this.streamingAgentHandoff = null;
          this.streamingTraceEvents = [];
          this.streamingProgressSteps = [];
          this.streamingFtcfArtifact = null;
          this.streamingFtcfFile = null;
          this.streamingDatalogChart = null;
          this.streamingDragRuns = [];
          this.streamingCharts = [];
          this.streamingMapArtifacts = [];
          this.streamingCorrectionArtifacts = [];
          this.streamAbortController = null;
          this.userScrolledUp = false;
          this.cdr.detectChanges();
          this.scrollToBottom();
        });
      }
    }
  }

  /** Vários `content` chegam no mesmo instante; o texto já foi aplicado, só o desenho espera o frame. */
  private scheduleStreamingRender(): void {
    if (this.streamingRenderFrame !== null) return;
    this.streamingRenderFrame = requestAnimationFrame(() => {
      this.streamingRenderFrame = null;
      this.cdr.detectChanges();
      this.scrollToBottom();
    });
  }

  private addErrorMessage(translationKey: string, params?: Record<string, unknown>, upgradeAction?: boolean, topUpAction?: boolean): void {
    this.messages.push({
      message_id: 'temp-error-' + Date.now(),
      role: 'assistant',
      content: this.translocoService.translate(translationKey, params),
      content_format: 'plain',
      meta: null,
      timestamp: new Date().toISOString(),
      reasoning_content: null,
      feedback_rating: null,
      dislike_reasons: null,
      is_guardrail_blocked: null,
      upgradeAction: upgradeAction ?? false,
      topUpAction: topUpAction ?? false,
    });
  }

  openPlansModal(): void {
    this.planModalService.open();
  }

  private handleAiHttpError(err: { status?: number; code?: string; details?: Record<string, unknown> }): void {
    const code = err.code;

    if (code?.startsWith('ai.rate_limit.')) {
      this.startRateLimitCountdown((err.details?.['retry_after'] as number) ?? 60);
      return;
    }
    if (code === AI_ERROR_CODES.TANK_EMPTY) {
      // `details.refill_at` sempre vem preenchido neste erro — é o que faz a mensagem ser
      // útil ("voltam às 16h20") em vez de só "sem créditos".
      const time = formatRefillTime(err.details?.['refill_at'] as string | undefined);
      this.addErrorMessage('chatbot.tankEmpty', { time }, true);
      return;
    }
    if (code === AI_ERROR_CODES.CREDITS_LIMIT_REACHED) {
      this.addErrorMessage('chatbot.creditsExhausted', {}, true); return;
    }
    if (code === AI_ERROR_CODES.SUBSCRIPTION_NOT_FOUND) {
      this.addErrorMessage('chatbot.subscriptionNotFound'); return;
    }
    if (code === AI_ERROR_CODES.SERVICE_AT_CAPACITY) {
      this.showRetryOption('chatbot.serviceAtCapacity'); return;
    }
    if (code === AI_ERROR_CODES.PROFILES_UNAVAILABLE) {
      // Kill-switch/catálogo ilegível — não é culpa do usuário, sem retry automático
      // (o botão de retry aqui é manual, disparado pelo próprio usuário).
      this.showRetryOption('chatbot.profilesUnavailable'); return;
    }
    if (code === AI_ERROR_CODES.ACCESS_NONE) {
      // A vitrine já carregada dá o CTA certo: se algum perfil está preso por saldo,
      // é recarga; senão (plano sem perfil de entrada) é upsell de plano.
      const noBalance = this.profileOptions.some(p => p.locked_reason === 'no_balance');
      this.addErrorMessage('chatbot.accessNone', {}, !noBalance, noBalance); return;
    }
    if (code === AI_ERROR_CODES.PROFILE_TOO_LONG) {
      this.addErrorMessage('chatbot.profileTooLong'); return;
    }

    // fallback legado — retrocompatibilidade com respostas sem code
    if (err.status === 402)      { this.addErrorMessage('chatbot.creditsExhausted', {}, true); }
    else if (err.status === 429) { this.startRateLimitCountdown(60); }
    else                         { this.addErrorMessage('chatbot.errorGeneric'); }
  }

  private startRateLimitCountdown(seconds: number): void {
    this.addErrorMessage('chatbot.rateLimitMessage', { seconds });
    this.rateLimitCountdown = seconds;
    this.rateLimitTimer = setInterval(() => {
      this.rateLimitCountdown--;
      if (this.rateLimitCountdown <= 0) {
        clearInterval(this.rateLimitTimer!);
        this.rateLimitTimer = null;
        this.rateLimitCountdown = 0;
      }
      this.cdr.detectChanges();
    }, 1000);
  }

  private showRetryOption(translationKey: string): void {
    this.addErrorMessage(translationKey);
    this.canRetry = true;
  }

  private addSessionToList(sessionId: string, firstMessage: string): void {
    const newSession: AiSession = {
      session_id: sessionId,
      title: firstMessage.substring(0, 50) + (firstMessage.length > 50 ? '...' : ''),
      last_message_preview: '',
      updated_at: new Date().toISOString(),
    };
    this.sessions.unshift(newSession);
  }

  private updateSessionPreview(_userMsg: string, assistantContent: string): void {
    const session = this.sessions.find(s => s.session_id === this.currentSessionId);
    if (session) {
      session.last_message_preview = assistantContent.substring(0, 80);
      session.updated_at = new Date().toISOString();
    }
  }

  // ─── Feedback ──────────────────────────────────────────────────────

  onFeedback(message: AiMessage, rating: 1 | -1): void {
    if (!this.currentSessionId || message.role !== 'assistant') return;
    if (message.feedback_rating === rating) return;

    if (rating === -1) {
      this.dislikeMessageId = message.message_id;
      this.dislikeReasons = [];
      this.dislikeComment = '';
      this.scrollDislikeFormIntoView();
      return;
    }

    this.feedbackLoadingId = message.message_id;
    this.aiService.sendFeedback({
      session_id: this.currentSessionId,
      message_id: message.message_id,
      rating,
    }).subscribe({
      next: () => {
        message.feedback_rating = rating;
        this.feedbackLoadingId = null;
      },
      error: () => {
        this.feedbackLoadingId = null;
      },
    });
  }

  toggleDislikeReason(reasonId: string): void {
    const idx = this.dislikeReasons.indexOf(reasonId);
    if (idx >= 0) {
      this.dislikeReasons.splice(idx, 1);
    } else {
      this.dislikeReasons.push(reasonId);
    }
  }

  submitDislike(): void {
    if (!this.currentSessionId || !this.dislikeMessageId) return;
    this.dislikeSending = true;

    const body: any = {
      session_id: this.currentSessionId,
      message_id: this.dislikeMessageId,
      rating: -1 as -1,
    };
    if (this.dislikeReasons.length > 0) body.dislike_reasons = this.dislikeReasons;
    if (this.dislikeComment.trim()) body.comment = this.dislikeComment.trim();

    this.aiService.sendFeedback(body).subscribe({
      next: () => {
        const msg = this.messages.find(m => m.message_id === this.dislikeMessageId);
        if (msg) msg.feedback_rating = -1;
        this.cancelDislike();
      },
      error: () => {
        this.dislikeSending = false;
      },
    });
  }

  cancelDislike(): void {
    this.dislikeMessageId = null;
    this.dislikeReasons = [];
    this.dislikeComment = '';
    this.dislikeSending = false;
  }

  /** Monta uma mensagem unica com todas as respostas. Onde "Outro" foi
   *  escolhido, entra o texto digitado no campo daquela pergunta. */
  sendClarification(): void {
    if (!this.canDispatchNow) return;
    const tr = (k: string) => this.translocoService.translate('chatbot.' + k);
    const questions = this.clarificationData?.questions ?? [];

    const lines: string[] = [tr('clarify.answersHeader')];
    for (const q of questions) {
      const other = (this.clarificationOtherMap[q.id] ?? '').trim();
      if (this.isFreeTextQuestion(q)) {
        if (other) lines.push(`- ${q.question}: ${other}`);
        continue;
      }
      const picked = (this.clarificationAnswers.get(q.id) ?? [])
        .map(l => (l === ChatbotComponent.OTHER ? other : l))
        .filter(Boolean);
      if (picked.length) lines.push(`- ${q.question}: ${picked.join(', ')}`);
    }

    this.userInput = lines.join('\n');
    this.clarificationData = null;
    this.clarificationAnswers = new Map();
    this.clarificationOtherMap = {};
    this.currentClarificationTab = 0;
    this.sendMessage();
  }

  toggleClarificationOption(questionId: string, label: string, multiSelect?: boolean): void {
    const current = this.clarificationAnswers.get(questionId) ?? [];
    if (multiSelect) {
      const idx = current.indexOf(label);
      if (idx >= 0) {
        this.clarificationAnswers.set(questionId, current.filter(l => l !== label));
      } else {
        this.clarificationAnswers.set(questionId, [...current, label]);
      }
    } else {
      this.clarificationAnswers.set(questionId, current[0] === label ? [] : [label]);
    }
  }

  /** Pergunta sem `options`: e texto livre puro. O backend manda varias assim
   *  (`allowOther: true` e nenhuma opcao) — 4 das 12 perguntas nos payloads
   *  reais de A/C. Sem tratar este caso, a pessoa via um unico botao "Outro" e
   *  precisava clica-lo para so entao poder escrever. */
  isFreeTextQuestion(q: ClarificationQuestion): boolean {
    return !q.options?.length;
  }

  isOtherSelected(questionId: string): boolean {
    return (this.clarificationAnswers.get(questionId) ?? []).includes(ChatbotComponent.OTHER);
  }

  toggleClarificationOther(questionId: string, multiSelect?: boolean): void {
    this.toggleClarificationOption(questionId, ChatbotComponent.OTHER, multiSelect);
    if (this.isOtherSelected(questionId)) {
      setTimeout(() => this.clarifyOtherEl?.nativeElement?.focus(), 0);
    } else {
      delete this.clarificationOtherMap[questionId];
    }
  }

  /** Botao de envio do chat. Fica fora durante o clarify: ali quem envia e o
   *  botao da propria caixa, para nao existirem dois caminhos para a mesma coisa. */
  get canSend(): boolean {
    if (!this.profilesEnabled || this.clarificationData || this.hasUploadingAttachment || this.isCommandText) return false;
    return !!this.userInput.trim() || this.hasReadyAttachment;
  }

  /** Todas as perguntas respondidas. So ai o envio libera. */
  get allClarificationAnswered(): boolean {
    const qs = this.clarificationData?.questions ?? [];
    return qs.length > 0 && qs.every(q => this.isClarificationQuestionAnswered(q));
  }

  /** O botao da caixa tem dois papeis: avanca enquanto falta pergunta, envia
   *  quando tudo esta respondido. Com uma pergunta so nunca ha o que avancar,
   *  entao ele ja nasce como envio. */
  get clarifyPrimaryIsSubmit(): boolean {
    return this.allClarificationAnswered || (this.clarificationData?.questions.length ?? 0) < 2;
  }

  get canClarifyPrimary(): boolean {
    return this.clarifyPrimaryIsSubmit ? this.allClarificationAnswered && !this.hasUploadingAttachment : true;
  }

  /** Proxima pergunta sem resposta a partir da atual, dando a volta. -1 quando
   *  nao ha nenhuma pendente. */
  private nextPendingClarificationIndex(): number {
    const qs = this.clarificationData?.questions ?? [];
    for (let step = 1; step <= qs.length; step++) {
      const i = (this.currentClarificationTab + step) % qs.length;
      if (!this.isClarificationQuestionAnswered(qs[i])) return i;
    }
    return -1;
  }

  /** Descarta o bloco de perguntas. A conversa segue na mesma sessao, mas a
   *  linha que a IA tinha aberto e abandonada: para continuar, a pessoa faz uma
   *  pergunta nova. Confirma so quando ha resposta a perder — pedir confirmacao
   *  para descartar uma caixa intocada seria atrito a toa. */
  dismissClarification(): void {
    const temResposta = (this.clarificationData?.questions ?? [])
      .some(q => this.isClarificationQuestionAnswered(q));

    if (!temResposta) {
      this.clearClarification();
      return;
    }

    this.confirmationService.confirm({
      message: this.translocoService.translate('chatbot.clarify.dismissConfirm'),
      header: this.translocoService.translate('chatbot.clarify.dismissHeader'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translocoService.translate('common.buttons.yes'),
      rejectLabel: this.translocoService.translate('common.buttons.no'),
      accept: () => {
        this.clearClarification();
        this.cdr.detectChanges();
      },
    });
  }

  private clearClarification(): void {
    this.clarificationData = null;
    this.clarificationAnswers = new Map();
    this.clarificationOtherMap = {};
    this.currentClarificationTab = 0;
    setTimeout(() => this.chatInputEl?.nativeElement?.focus(), 0);
  }

  onClarifyPrimary(): void {
    if (this.clarifyPrimaryIsSubmit) {
      if (this.allClarificationAnswered) this.sendClarification();
      return;
    }
    const next = this.nextPendingClarificationIndex();
    if (next >= 0) this.setClarificationTab(next);
  }

  /** Unico ponto de troca de aba. Traz a aba para a area visivel: com muitas
   *  perguntas a tira rola na horizontal, e ao avancar sozinho a aba ativa
   *  podia ficar fora de vista — o conteudo mudava sem indicacao de onde. */
  setClarificationTab(index: number): void {
    this.currentClarificationTab = index;
    setTimeout(() => {
      this.clarifyTabEls?.get(index)?.nativeElement
        ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }, 0);
  }

  /** Escolha de opcao normal. Avanca sozinho para a proxima pendente, que e o
   *  que a pessoa faria em seguida de qualquer jeito. Nao avanca em multipla
   *  escolha (ela ainda pode estar marcando outras) nem ao desmarcar. */
  pickClarificationOption(q: ClarificationQuestion, label: string): void {
    this.toggleClarificationOption(q.id, label, q.multiSelect);
    if (q.multiSelect) return;
    if ((this.clarificationData?.questions.length ?? 0) < 2) return;
    if (!this.isClarificationQuestionAnswered(q)) return;
    const next = this.nextPendingClarificationIndex();
    if (next >= 0) this.setClarificationTab(next);
  }

  isOptionSelected(questionId: string, label: string): boolean {
    return (this.clarificationAnswers.get(questionId) ?? []).includes(label);
  }




  isClarificationQuestionAnswered(q: ClarificationQuestion): boolean {
    const text = (this.clarificationOtherMap[q.id] ?? '').trim();
    if (this.isFreeTextQuestion(q)) return !!text;

    const picked = this.clarificationAnswers.get(q.id) ?? [];
    if (!picked.length) return false;
    // "Outro" sem texto nao conta como resposta.
    if (picked.includes(ChatbotComponent.OTHER) && !text) {
      return picked.length > 1;
    }
    return true;
  }

  answeredClarificationCount(): number {
    return (this.clarificationData?.questions ?? []).filter(q => this.isClarificationQuestionAnswered(q)).length;
  }

  getDisplayContent(content: string, role: string): string {
    if (role !== 'user') return content;
    return clarificationAnswersForDisplay(content) ?? content;
  }

  downloadFtcf(file: FtcfFile): void {
    const blob = new Blob([file.content], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${file.title}.ftcf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async downloadConversationJson(): Promise<void> {
    if (!this.currentSessionId || this.isExportingConversation) return;
    const sessionId = this.currentSessionId;
    this.isExportingConversation = true;

    try {
      let cursor: string | null = null;
      let session: AiSessionDetailSession | null = null;
      let allMessages: AiMessage[] = [];

      do {
        const page: AiSessionDetail = await lastValueFrom(this.aiService.getSession(sessionId, 100, cursor));
        session = session ?? page.session;
        allMessages = allMessages.concat(page.messages);
        cursor = page.next_cursor;
      } while (cursor);

      const data = { session, messages: allMessages };
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `conversa-${session?.title || sessionId}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      this.messageService.add({
        key: 'notification', severity: 'error',
        summary: this.translocoService.translate('chatbot.exportError'),
      });
    } finally {
      this.isExportingConversation = false;
    }
  }

  toggleThinking(messageId: string): void {
    if (this.expandedThinkingIds.has(messageId)) {
      this.expandedThinkingIds.delete(messageId);
    } else {
      this.expandedThinkingIds.add(messageId);
    }
  }

  // ─── Progress (narração pública) rendering ─────────────────────────

  /** Expande/colapsa a lista de etapas de progresso (pílula "Concluído"). */
  /**
   * Insere/atualiza a etapa de progresso mantendo a ordem: concluídas (✓) no
   * topo, na ordem em que terminaram; as que ainda carregam ficam sempre embaixo.
   * Assim, quando uma que estava carregando conclui, ela sobe acima das que
   * ainda estão pensando.
   */
  private upsertProgressStep(p: ProgressStep): void {
    const list = this.streamingProgressSteps.filter((s) => s.step !== p.step);
    if (p.status === 'done') {
      // Insere logo após as já concluídas (prefixo contíguo) → ordem de conclusão.
      const doneCount = list.filter((s) => s.status === 'done').length;
      list.splice(doneCount, 0, p);
    } else {
      // Ainda carregando → sempre no fim (embaixo).
      list.push(p);
    }
    this.streamingProgressSteps = list;
  }

  toggleProgress(id: string): void {
    if (this.expandedProgressIds.has(id)) {
      this.expandedProgressIds.delete(id);
    } else {
      this.expandedProgressIds.add(id);
    }
  }

  isProgressExpanded(id: string): boolean {
    return this.expandedProgressIds.has(id);
  }

  // ─── Input handling ────────────────────────────────────────────────

  onInputKeydown(event: KeyboardEvent): void {
    if (this.actionsMode === 'typed') {
      this.onActionsKeydown(event);
      return;
    }
    if (event.key === 'Enter' && !event.shiftKey && this.userInput.trim()) {
      this.sendMessage();
      event.preventDefault();
    }
  }

  // ─── Scroll ────────────────────────────────────────────────────────

  private scrollToBottom(): void {
    if (this.userScrolledUp) return;
    setTimeout(() => {
      if (this.chatScrollEl?.nativeElement) {
        const el = this.chatScrollEl.nativeElement;
        el.scrollTop = el.scrollHeight;
      }
    }, 50);
  }

  private scrollDislikeFormIntoView(): void {
    setTimeout(() => {
      const form = this.chatScrollEl?.nativeElement?.querySelector('.dislike-form');
      form?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 50);
  }
}
